'use client';

import { useCallback, useEffect, useState } from 'react';

import { useCommunityContext } from '../contexts/community/useCommunityContext';
import { fetchChannelMemberCount } from '../functions/members/fetchChannelMemberCount';

interface Counts {
  /** Non-blocked members (All tab); `undefined` until first load. */
  all: number | undefined;
  /** Blocked members (Blocked tab); `undefined` until first load. */
  blocked: number | undefined;
  /** Re-fetch both counts — call after a block/unblock. */
  refresh: () => void;
}

/**
 * All + Blocked member counts for the members-panel tab labels
 * ("All (N)" / "Blocked (N)"), mirroring PopshopRN's two
 * `useCommunityChannelMemberCount` reads. Both are refreshed together after a
 * moderation action so a member moving between tabs updates both totals.
 */
export function useChannelMemberCounts(
  channelId: string | null | undefined,
): Counts {
  const { creatorId } = useCommunityContext();
  const [counts, setCounts] = useState<{
    all: number | undefined;
    blocked: number | undefined;
  }>({ all: undefined, blocked: undefined });
  const [reloadIndex, setReloadIndex] = useState(0);
  const refresh = useCallback(() => setReloadIndex(index => index + 1), []);

  useEffect(() => {
    if (!creatorId || !channelId) {
      setCounts({ all: undefined, blocked: undefined });
      return;
    }
    let cancelled = false;
    // "All" counts non-blocked members (`blocked: false`) so the label matches
    // the All list, which excludes blocked members.
    const all = fetchChannelMemberCount(creatorId, channelId, {
      blocked: false,
    });
    const blocked = fetchChannelMemberCount(creatorId, channelId, {
      blocked: true,
    });
    all
      .then(value => {
        if (!cancelled) setCounts(prev => ({ ...prev, all: value }));
      })
      .catch((error: unknown) =>
        console.error('failed to load channel member count (all):', error),
      );
    blocked
      .then(value => {
        if (!cancelled) setCounts(prev => ({ ...prev, blocked: value }));
      })
      .catch((error: unknown) =>
        console.error('failed to load channel member count (blocked):', error),
      );
    return () => {
      cancelled = true;
    };
  }, [creatorId, channelId, reloadIndex]);

  return { ...counts, refresh };
}
